import { Component } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import config from 'constants/config'
import GoogleLoginButton from './GoogleLoginButton'
import FacebookLoginButton from './FacebookLoginButton'
import AppleLoginButton from './AppleLoginButton'

import {
  register,
  loginFacebook,
  loginGoogle,
  loginApple,
} from 'actions/AuthActions'

import { modalOpen, modalClose } from 'actions/UiActions'
import Modal from 'LuxKit/Modal/Modal'

export {
  SOCIAL_LOGIN_ICON_BUTTON_PROPS,
  SOCIAL_LOGIN_TEXT_BUTTON_PROPS,
} from './SocialLoginButton.constants'

function getInitialState(props: Props) {
  const {
    mode,
    source,
    utm,
  } = props

  switch (source) {
    case 'facebook':
      return {
        utm: 'utm_fb',
        mode: utm.fb || mode || 'login',
      }
    case 'google':
      return {
        utm: 'utm_google',
        mode: utm.google || mode || 'login',
      }
    case 'apple':
      return {
        utm: 'utm_apple',
        mode: utm.apple || mode || 'login',
      }
    default:
      return {}
  }
}

interface Props {
  intl: IntlShape
  source: Exclude<App.AuthSource, 'email' | 'otp'>
  mode: 'login' | 'join'
  customerPortalURL: string
  regionCode: string
  utm: App.UTMState
  modalError: any
  currentUrl: string
  authError: any
  isBlocked?: boolean
  iconOnly?: boolean
  className?: string
  loginFacebook: (...params: Parameters<typeof loginFacebook>) => void
  loginGoogle: (...params: Parameters<typeof loginGoogle>) => void
  loginApple: (...params: Parameters<typeof loginApple>) => void
  register: (...params: Parameters<typeof register>) => void
  modalOpen: (...params: Parameters<typeof modalOpen>) => void
  modalClose: (...params: Parameters<typeof modalClose>) => void
  onPrivateAppleEmailLogin?: (appleLoginData: App.AppleLoginData, email: string) => void
}

class SocialLoginButton extends Component<React.PropsWithChildren<Props>> {
  state = getInitialState(this.props)

  redirectUri = (baseUrl = false) => {
    const {
      customerPortalURL,
      currentUrl,
    } = this.props

    return `${customerPortalURL}${baseUrl ? '' : currentUrl}?${this.state.utm}=${this.state.mode}`
  }

  onShowError = (error: string) => {
    const { intl } = this.props
    if (error === 'popup_blocked_by_browser') {
      const title = intl.formatMessage({
        id: '3poeMiwGV0pa4J1tDYHa',
        defaultMessage: 'Popup blocked by your browser',
        description: 'Error modal title when browser blocks social login popup',
      })
      const body = intl.formatMessage({
        id: 'BbsWJyIPk0mAYHB3XGsp',
        defaultMessage: 'Please make sure that you allow popups on this site',
        description: 'Error modal body when browser blocks social login popup',
      })
      this.props.modalOpen(title, body)
    } else if (error === 'access_denied') {
      const title = intl.formatMessage({
        id: '73LXFaJVp8rUkrrsjRif',
        defaultMessage: 'Something went wrong',
        description: 'Error modal title when social login fails dialog window',
      })
      const body = intl.formatMessage({
        id: 'xIg_HXID1KwAoE0Ml4km',
        defaultMessage: 'An error occurred while trying to login, please try again later',
        description: 'Error modal body when social login fails dialog window',
      })
      this.props.modalOpen(title, body)
    }
  }

  onFailure = ({ error }: any) => {
    this.onShowError(error)
  }

  onSuccess = (data: any) => {
    if (this.state.mode === 'join') {
      this.props.register(data, this.props.source)
    } else {
      if (this.props.source === 'google') {
        this.props.loginGoogle(data)
      } else if (this.props.source === 'facebook') {
        this.props.loginFacebook(data)
      } else if (this.props.source === 'apple') {
        this.props.loginApple(data)
      }
    }
  }

  render() {
    const {
      isBlocked,
      className,
      modalError,
      modalClose,
      source,
      iconOnly,
      onPrivateAppleEmailLogin,
    } = this.props

    return (
      <>
        {source === 'apple' && config.APPLE_APP_ID &&
          <AppleLoginButton
            isBlocked={isBlocked}
            className={className}
            redirectUri={this.redirectUri(true)}
            onSuccess={this.onSuccess}
            onFailure={this.onFailure}
            iconOnly={iconOnly}
            onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
          />}
        {source === 'facebook' && config.FACEBOOK_APP_ID &&
          <FacebookLoginButton
            isBlocked={isBlocked}
            className={className}
            onSuccess={this.onSuccess}
            iconOnly={iconOnly}
          />}
        {source === 'google' && config.GOOGLE_APP_ID &&
          <GoogleLoginButton iconOnly={iconOnly} />}
        {modalError.open &&
          <Modal
            isOpen={modalError.open}
            onClose={modalClose}
            title={modalError.heading ?? ''}
          >
            {modalError.message}
          </Modal>}
      </>
    )
  }
}

const mapStateToProps = (state: App.State) => ({
  customerPortalURL: state.system.customerPortalURL,
  regionCode: state.geo.currentRegionCode,
  utm: state.utm,
  modalError: state.ui.modal.error,
  currentUrl: state.router.location.pathname,
  authError: state.auth.error,
})

const mapDispatchToProps = {
  loginFacebook,
  loginGoogle,
  loginApple,
  register,
  modalOpen,
  modalClose,
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SocialLoginButton))
